import { useMemo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../utils/cn';

/* ──────────────── Build Tree ──────────────── */

/**
 * Convert flat reporting lines into a tree structure.
 * Each node: { email, name, children: [], directReports: number, ... }
 * Root nodes are people who report to themselves.
 */
function buildTree(reportingLines, compensations) {
  const nodeMap = {}; // email → node

  // Index compensations by employee email
  const compByEmail = {};
  (compensations || []).forEach((c) => {
    if (c.employeeEmail) compByEmail[c.employeeEmail.toLowerCase()] = c;
  });

  // Index communicators: which employees does each communicator have?
  const commMap = {}; // communicatorEmail → Set of employeeEmails
  (compensations || []).forEach((c) => {
    if (c.communicatorEmail) {
      const key = c.communicatorEmail.toLowerCase();
      if (!commMap[key]) commMap[key] = new Set();
      commMap[key].add(c.employeeEmail?.toLowerCase());
    }
  });

  // Create all nodes
  for (const line of reportingLines) {
    const email = line.employeeEmail?.toLowerCase();
    if (!email) continue;
    if (!nodeMap[email]) {
      nodeMap[email] = {
        email,
        name: line.employeeName || email.split('@')[0],
        children: [],
        supervisorEmail: line.supervisorEmail?.toLowerCase(),
        isRoot: email === line.supervisorEmail?.toLowerCase(),
      };
    } else {
      // Update name from reporting line if available
      nodeMap[email].name = line.employeeName || nodeMap[email].name;
      nodeMap[email].supervisorEmail = line.supervisorEmail?.toLowerCase();
      nodeMap[email].isRoot = email === line.supervisorEmail?.toLowerCase();
    }
  }

  // Enrich with compensation data
  for (const node of Object.values(nodeMap)) {
    const comp = compByEmail[node.email];
    node.hasCompensation = !!comp;
    node.department = comp?.department || '';
    node.recipientCount = commMap[node.email]?.size || 0;
    node.isCommunicator = (commMap[node.email]?.size || 0) > 0;
  }

  // Build parent→children relationships
  for (const node of Object.values(nodeMap)) {
    if (!node.isRoot && node.supervisorEmail && nodeMap[node.supervisorEmail]) {
      nodeMap[node.supervisorEmail].children.push(node);
    }
  }

  // Sort children alphabetically
  for (const node of Object.values(nodeMap)) {
    node.children.sort((a, b) => a.name.localeCompare(b.name));
    node.directReports = node.children.length;
  }

  // Find roots (those who report to themselves)
  const roots = Object.values(nodeMap).filter((n) => n.isRoot);
  roots.sort((a, b) => a.name.localeCompare(b.name));

  return { roots, nodeMap, totalNodes: Object.keys(nodeMap).length };
}

/* ──────────────── Count descendants ──────────────── */

function countDescendants(node) {
  let count = 0;
  for (const child of node.children) {
    count += 1 + countDescendants(child);
  }
  return count;
}

/* ──────────────── Tree Node Component ──────────────── */

function TreeNode({ node, depth = 0, isLast = false, searchTerm = '' }) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children.length > 0;
  const descendantCount = useMemo(() => countDescendants(node), [node]);

  const matchesSearch = searchTerm
    ? node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      node.email.toLowerCase().includes(searchTerm.toLowerCase())
    : true;

  const hasMatchingDescendant = useMemo(() => {
    if (!searchTerm) return true;
    const check = (n) => {
      if (n.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          n.email.toLowerCase().includes(searchTerm.toLowerCase())) return true;
      return n.children.some(check);
    };
    return check(node);
  }, [node, searchTerm]);

  if (searchTerm && !matchesSearch && !hasMatchingDescendant) return null;

  const toggleExpand = useCallback((e) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  }, []);

  return (
    <div className="relative">
      {/* Vertical line from parent */}
      {depth > 0 && (
        <div className={cn(
          'absolute left-[-20px] top-0 w-[1px] bg-surface-300',
          isLast ? 'h-[28px]' : 'h-full',
        )} />
      )}
      {/* Horizontal line to node */}
      {depth > 0 && (
        <div className="absolute left-[-20px] top-[28px] w-[20px] h-[1px] bg-surface-300" />
      )}

      {/* Node card */}
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: Math.min(depth * 0.03, 0.3), duration: 0.3 }}
        className={cn(
          'group flex items-center gap-3 rounded-xl border px-3.5 py-2.5 mb-1.5 transition-all cursor-pointer select-none',
          node.isRoot
            ? 'bg-gradient-to-r from-accent-50 to-blue-50 border-accent-200 shadow-sm hover:shadow-md'
            : 'bg-white border-surface-200 hover:border-accent-300 hover:shadow-sm',
          searchTerm && matchesSearch && 'ring-2 ring-accent-400 ring-offset-1',
        )}
        onClick={toggleExpand}
      >
        {/* Avatar */}
        <NodeAvatar name={node.name} isRoot={node.isRoot} />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={cn(
              'text-sm font-semibold truncate',
              node.isRoot ? 'text-accent-800' : 'text-navy-900',
            )}>
              {node.name}
            </p>
            {node.isRoot && (
              <span className="shrink-0 inline-flex items-center rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-bold text-accent-700 uppercase tracking-wider">
                Root
              </span>
            )}
          </div>
          <p className="text-[11px] text-navy-400 truncate">{node.email}</p>
        </div>

        {/* Badges / stats */}
        <div className="flex items-center gap-2 shrink-0">
          {node.recipientCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>
              {node.recipientCount} recipient{node.recipientCount !== 1 ? 's' : ''}
            </span>
          )}
          {node.isCommunicator && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" /></svg>
              communicator
            </span>
          )}
          {hasChildren && (
            <span className="inline-flex items-center gap-1 rounded-full bg-surface-100 border border-surface-200 px-2 py-0.5 text-[11px] font-semibold text-navy-500">
              {node.directReports} direct · {descendantCount} total
            </span>
          )}
          {hasChildren && (
            <motion.button
              animate={{ rotate: expanded ? 90 : 0 }}
              transition={{ duration: 0.2 }}
              className="p-1 rounded-lg text-navy-400 hover:text-navy-600 hover:bg-surface-100 transition-colors"
              onClick={toggleExpand}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </motion.button>
          )}
        </div>
      </motion.div>

      {/* Children */}
      <AnimatePresence>
        {hasChildren && expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="ml-8 pl-5 relative overflow-hidden"
          >
            {node.children.map((child, i) => (
              <TreeNode
                key={child.email}
                node={child}
                depth={depth + 1}
                isLast={i === node.children.length - 1}
                searchTerm={searchTerm}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Node Avatar ── */
function NodeAvatar({ name, isRoot }) {
  const initials = (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  const hash = (name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const colors = [
    'from-accent-400 to-accent-600',
    'from-cyan-400 to-cyan-600',
    'from-emerald-400 to-emerald-600',
    'from-amber-400 to-amber-600',
    'from-rose-400 to-rose-600',
    'from-blue-400 to-blue-600',
  ];
  const gradient = isRoot ? 'from-accent-500 to-blue-600' : colors[hash % colors.length];

  return (
    <div className={cn(
      'shrink-0 rounded-full bg-gradient-to-br flex items-center justify-center text-white font-bold',
      isRoot ? 'w-10 h-10 text-sm' : 'w-8 h-8 text-xs',
      gradient,
    )}>
      {initials}
    </div>
  );
}

/* ──────────────── Main OrgTreeView Component ──────────────── */

export default function OrgTreeView({ reportingLines = [], compensations = [] }) {
  const [search, setSearch] = useState('');
  const [expandAll, setExpandAll] = useState(false);

  const { roots, totalNodes } = useMemo(
    () => buildTree(reportingLines, compensations),
    [reportingLines, compensations],
  );

  const stats = useMemo(() => {
    const supervisorEmails = new Set(reportingLines.map((r) => r.supervisorEmail?.toLowerCase()));
    const communicatorEmails = new Set(
      (compensations || []).filter((c) => c.communicatorEmail).map((c) => c.communicatorEmail.toLowerCase()),
    );
    return {
      total: totalNodes,
      supervisors: supervisorEmails.size,
      communicators: communicatorEmails.size,
      roots: roots.length,
    };
  }, [reportingLines, compensations, totalNodes, roots]);

  if (reportingLines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 rounded-2xl bg-surface-100 flex items-center justify-center text-3xl mb-4">
          🏢
        </div>
        <h3 className="text-lg font-bold text-navy-900 mb-1">No reporting structure yet</h3>
        <p className="text-sm text-navy-400 mb-4">
          Import a CSV with employee-supervisor relationships to visualize the org chart.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total People', value: stats.total, icon: '👥' },
          { label: 'Supervisors', value: stats.supervisors, icon: '👤' },
          { label: 'Communicators', value: stats.communicators, icon: '💬' },
          { label: 'Root Nodes', value: stats.roots, icon: '🏢' },
        ].map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            className="bg-white rounded-2xl border border-surface-200/80 p-4 shadow-subtle"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-base">{s.icon}</span>
              <p className="text-xs font-semibold text-navy-400 uppercase tracking-wider">{s.label}</p>
            </div>
            <p className="text-xl font-bold text-navy-900">{s.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Search & controls */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-surface-200 bg-white text-sm text-navy-900 placeholder:text-navy-300 focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-400 transition-all"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded text-navy-300 hover:text-navy-600 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <button
          onClick={() => setExpandAll((v) => !v)}
          className="px-3 py-2.5 rounded-xl border border-surface-200 bg-white text-xs font-semibold text-navy-600 hover:bg-surface-50 hover:border-accent-300 transition-all flex items-center gap-1.5"
        >
          {expandAll ? (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
              Collapse
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
              Expand All
            </>
          )}
        </button>
      </div>

      {/* Tree */}
      <div className="bg-white rounded-2xl border border-surface-200/80 p-5 shadow-subtle overflow-x-auto">
        <TreeView roots={roots} search={search} expandAll={expandAll} />
      </div>
    </div>
  );
}

/* ── TreeView renderer ── */
function TreeView({ roots, search, expandAll }) {
  // Use a key that changes when expandAll changes to force re-render with new default state
  return (
    <div key={expandAll ? 'expanded' : 'collapsed'}>
      {roots.map((root) => (
        <TreeNode
          key={root.email}
          node={root}
          depth={0}
          searchTerm={search}
        />
      ))}
    </div>
  );
}
